import { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAdminStore } from '../../stores/adminStore'
import { useAuthStore } from '../../stores/authStore'
import { Admin, AdminRole, AdminRoleLabels } from '../../models/User'

const roleColors: Record<AdminRole, 'error' | 'primary' | 'warning' | 'default'> = {
  super_admin: 'error',
  admin: 'primary',
  senior_assistant: 'warning',
  assistant: 'default',
}

export default function AdminListPage() {
  const { admins, loading, error, fetchAdmins, createAdmin, updateAdmin, deleteAdmin } =
    useAdminStore()
  const { admin: currentAdmin } = useAuthStore()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'assistant' as AdminRole,
    isActive: true,
  })
  const [formError, setFormError] = useState('')

  useEffect(() => {
    fetchAdmins()
  }, [fetchAdmins])

  const handleOpenCreate = () => {
    setFormData({
      email: '',
      password: '',
      displayName: '',
      role: 'assistant',
      isActive: true,
    })
    setFormError('')
    setCreateDialogOpen(true)
  }

  const handleOpenEdit = (admin: Admin) => {
    setSelectedAdmin(admin)
    setFormData({
      email: admin.email,
      password: '',
      displayName: admin.displayName,
      role: admin.role,
      isActive: admin.isActive,
    })
    setFormError('')
    setEditDialogOpen(true)
  }

  const handleOpenDelete = (admin: Admin) => {
    setSelectedAdmin(admin)
    setDeleteDialogOpen(true)
  }

  const handleCreate = async () => {
    if (!formData.email || !formData.password || !formData.displayName) {
      setFormError('請填寫所有必填欄位')
      return
    }
    try {
      await createAdmin({
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
        role: formData.role,
      })
      setCreateDialogOpen(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '建立失敗')
    }
  }

  const handleUpdate = async () => {
    if (!selectedAdmin) return
    try {
      await updateAdmin(selectedAdmin.id, {
        displayName: formData.displayName,
        role: formData.role,
        isActive: formData.isActive,
      })
      setEditDialogOpen(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '更新失敗')
    }
  }

  const handleDelete = async () => {
    if (!selectedAdmin) return
    try {
      await deleteAdmin(selectedAdmin.id)
      setDeleteDialogOpen(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '刪除失敗')
    }
  }

  if (loading && admins.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          管理員設定
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          新增管理員
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名稱</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>建立時間</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>{admin.displayName}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>
                  <Chip
                    label={AdminRoleLabels[admin.role]}
                    color={roleColors[admin.role]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={admin.isActive ? '啟用' : '停用'}
                    color={admin.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {admin.createdAt?.toLocaleDateString('zh-TW')}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEdit(admin)}
                    disabled={admin.id === currentAdmin?.id}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleOpenDelete(admin)}
                    disabled={admin.id === currentAdmin?.id}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增管理員</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="名稱"
            fullWidth
            margin="normal"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            label="密碼"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>角色</InputLabel>
            <Select
              value={formData.role}
              label="角色"
              onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminRole })}
            >
              <MenuItem value="admin">老師</MenuItem>
              <MenuItem value="senior_assistant">高級助理</MenuItem>
              <MenuItem value="assistant">助理</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleCreate} disabled={loading}>
            建立
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>編輯管理員</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="名稱"
            fullWidth
            margin="normal"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={formData.email}
            disabled
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>角色</InputLabel>
            <Select
              value={formData.role}
              label="角色"
              onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminRole })}
            >
              <MenuItem value="admin">老師</MenuItem>
              <MenuItem value="senior_assistant">高級助理</MenuItem>
              <MenuItem value="assistant">助理</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="啟用帳號"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={loading}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除管理員「{selectedAdmin?.displayName}」嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
            刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
